import type { PromptInputFilePart } from '@/components/ai-elements/prompt-input'
import { isEmailSourceDataUrl } from '@/lib/email-conversion/source-formats/email-source-data-url'
import { stageEmailSourceAttachment } from '@/lib/email-conversion/source-formats/read-email-source-file'
import type { SubmittedMessageFile } from '@/lib/shared/chat-message-builders'

import {
  createPublicComposerFilesKey,
  deletePublicComposerDraftFiles,
  type PublicComposerLocalFile,
  readPublicComposerDraftFiles,
  writePublicComposerDraftFiles,
} from './public-composer-draft'

/**
 * The launch composer hands a prompt to the chat thread through
 * localStorage. An inline email source file is a multi-megabyte `data:` URL
 * that would blow the quota (and `writePendingPrompt` swallows the failure,
 * losing the prompt), so those files are stashed as `File` objects in the
 * IndexedDB draft store and re-staged when the thread consumes the slot.
 */

type SplitPendingPromptFiles = {
  /** Small enough for localStorage (hosted URLs). */
  stored: Array<PromptInputFilePart>
  /** Inline email sources — stashed by `filesKey`. */
  stashed: Array<PublicComposerLocalFile>
}

export function splitPendingPromptFiles(
  files: ReadonlyArray<PromptInputFilePart>
): SplitPendingPromptFiles {
  const stored: Array<PromptInputFilePart> = []
  const stashed: Array<PublicComposerLocalFile> = []
  files.forEach((file, index) => {
    if (isEmailSourceDataUrl(file.url) && file.file) {
      stashed.push({
        id: `${index}`,
        file: file.file,
        ...(file.previewText ? { previewText: file.previewText } : {}),
      })
      return
    }
    stored.push(file)
  })
  return { stored, stashed }
}

/** Stash the inline files for `chatId`; returns the key to put on the slot. */
export async function stashPendingPromptFiles(
  chatId: string,
  stashed: ReadonlyArray<PublicComposerLocalFile>
): Promise<string | undefined> {
  if (stashed.length === 0) {
    return
  }
  const filesKey = createPublicComposerFilesKey(chatId)
  await writePublicComposerDraftFiles(filesKey, [...stashed])
  return filesKey
}

/** Re-read the stash and stage each file inline again (no network). */
export async function restagePendingPromptFiles(
  filesKey: string | undefined
): Promise<Array<SubmittedMessageFile>> {
  if (!filesKey) {
    return []
  }
  const stashed = await readPublicComposerDraftFiles(filesKey)
  const staged: Array<SubmittedMessageFile> = []
  for (const entry of stashed) {
    try {
      // Sequential on purpose: one file at a time keeps peak memory bounded.
      // biome-ignore lint/performance/noAwaitInLoops: bounded, memory-aware
      const attachment = await stageEmailSourceAttachment(entry.file)
      staged.push({
        url: attachment.url,
        mediaType: attachment.mediaType,
        filename: attachment.filename,
        previewText: attachment.previewText,
      })
    } catch {
      // A file that no longer stages (over the limit, unreadable) is dropped;
      // the prompt text still submits.
    }
  }
  return staged
}

export async function discardPendingPromptFiles(
  filesKey: string | undefined
): Promise<void> {
  await deletePublicComposerDraftFiles(filesKey)
}
